<template>
  <footer class="tw-text-sm tw-font-normal text-dark-color">
    <small
      v-if="canShowStatus"
      class="tw-block"
    >
      Resort Status:
      <span :class="['tw-font-bold', status.className]">
        {{ status.text }}
      </span>
    </small>
    <small :class="reportedClass">
      <EstimatedBadge
        v-if="isEstimate"
        size="sm"
      />
      <span v-else>{{ timeStampLabel }}</span>
      {{ preposition }} {{ timeStampValue }}
      <font-awesome-icon
        v-tooltip="vTooltipOptions"
        class="tw-ml-1"
        :icon="['far', 'info-circle']"
      />
    </small>
  </footer>
</template>

<script>
/* eslint camelcase: off */
import { mapState } from 'pinia';
import {
  dataNotAvailable,
  formatForecastDate,
  getAreaPercentOpen,
  getClosedText,
  getLastItem,
  getLastItemWithPrecipSnow,
  hasValidResortReport,
  isClosed,
  isNotClosed,
  isOld,
} from '@@/utils/CommonUtils';
import { useMetaStore } from '@@/stores/Meta';

export const viewTypes = {
  currentWeather: 'currentWeather',
  forecast: 'forecast',
  history: 'history',
  historySnowSeasonal: 'historySnowSeasonal',
  newSnow: 'newSnow',
  resortReport: 'resortReport',
  snowDepth: 'snowDepth',
};

export default {
  name: 'SnowLocationFooter',

  props: {
    historyLocation: {
      type: Object,
      required: false,
      default() {
        return null;
      },
    },
    location: {
      type: Object,
      required: true,
    },
    viewType: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(viewTypes).includes(value);
      },
    },
  },

  computed: {
    ...mapState(useMetaStore, [
      'dataSourcesById',
      'getAttribution',
      'locationTypesById',
    ]),

    canShowStatus() {
      return this.viewType === viewTypes.newSnow && hasValidResortReport(this.location);
    },

    dataSource() {
      let lastItem;
      let source_id;

      switch (this.viewType) {
        case viewTypes.currentWeather:
        case viewTypes.forecast:
          source_id = this.location.forecast_source_id;
          break;

        case viewTypes.newSnow:
          lastItem = getLastItem(this.location.history_snow_daily);
          ({ source_id } = lastItem || {});
          break;

        case viewTypes.history:
          lastItem = this.historyLocation
            ? getLastItemWithPrecipSnow(this.historyLocation?.history_snow_daily)
            : getLastItem(this.location.history_snow_daily);
          ({ source_id } = lastItem || {});
          break;

        case viewTypes.historySnowSeasonal:
          lastItem = getLastItem(this.location.history_snow_seasonal, {});
          ({ source_id } = lastItem);
          break;

        case viewTypes.resortReport:
          ({ source_id } = this.location?.resort_report || {});
          break;

        case viewTypes.snowDepth:
          ({ source_id } = this.location?.snow_depth_seasonal || {});
          break;

        default:
          break;
      }

      return this.dataSourcesById[source_id] || {};
    },

    isEstimate() {
      if (this.viewType === viewTypes.forecast || this.viewType === viewTypes.currentWeather) {
        return false;
      }

      return this.dataSource?.is_estimate || false;
    },

    isOld() {
      return isOld(this.timeStampDisplayAt);
    },

    preposition() {
      return this.viewType === viewTypes.currentWeather ? '' : 'at';
    },

    reportedClass() {
      return [
        'tw-block',
        this.isEstimate && this.canShowStatus ? 'lg:tw-pt-1' : undefined,
        this.isOld ? 'text-saturated-red-color' : undefined,
      ];
    },

    status() {
      const { resort_report } = this.location;
      const { status_display } = resort_report;

      if (isClosed(resort_report)) {
        return {
          className: 'text-saturated-red-color',
          text: getClosedText(resort_report),
        };
      }

      if (isNotClosed(resort_report)) {
        const areaPercentOpen = resort_report?.percent_open > 0
          ? getAreaPercentOpen(resort_report)
          : null;

        return {
          className: 'text-green-color',
          text: areaPercentOpen ? `Open (${areaPercentOpen})` : 'Open',
        };
      }

      return {
        className: 'text-darkest-color',
        text: status_display,
      };
    },

    timeStampLabel() {
      if (this.viewType === viewTypes.historySnowSeasonal
        || this.viewType === viewTypes.newSnow
        || this.viewType === viewTypes.snowDepth) {
        return this.isEstimate ? '' : 'Reported';
      }

      if (this.viewType === viewTypes.history
        || this.viewType === viewTypes.forecast) {
        return 'Updated';
      }

      if (this.viewType === viewTypes.resortReport) {
        return 'Reported';
      }

      return '';
    },

    timeStampDisplayAt() {
      if (this.viewType === viewTypes.newSnow) {
        const { display_at } = getLastItem(this.location.history_snow_daily, {});
        return display_at;
      }

      if (this.viewType === viewTypes.history) {
        const { display_at } = this.historyLocation
          ? getLastItemWithPrecipSnow(this.historyLocation.history_snow_daily, {})
          : getLastItem(this.location.history_snow_daily, {});

        return display_at;
      }

      if (this.viewType === viewTypes.forecast) {
        return this.location.forecast_updated_at;
      }

      if (this.viewType === viewTypes.currentWeather) {
        return this.location.forecast_current?.display_at;
      }

      if (this.viewType === viewTypes.snowDepth) {
        return this.location.snow_depth_seasonal?.display_at;
      }

      if (this.viewType === viewTypes.resortReport) {
        return this.location.resort_report?.display_at;
      }

      return null;
    },

    timeStampValue() {
      if (this.viewType === viewTypes.newSnow
        || this.viewType === viewTypes.history
        || this.viewType === viewTypes.forecast
        || this.viewType === viewTypes.currentWeather) {
        return this.timeStampDisplayAt
          ? formatForecastDate(this.timeStampDisplayAt, this.location.timezone)
          : dataNotAvailable;
      }

      if (this.viewType === viewTypes.historySnowSeasonal) {
        const { display_at_local_label } = getLastItem(this.location.history_snow_seasonal, {});
        return display_at_local_label || dataNotAvailable;
      }

      if (this.viewType === viewTypes.snowDepth) {
        return this.location.snow_depth_seasonal?.display_at_local_label || dataNotAvailable;
      }

      if (this.viewType === viewTypes.resortReport) {
        return this.location.resort_report?.display_at_local_label || dataNotAvailable;
      }

      return '';
    },

    vTooltipOptions() {
      const { name = 'Unknown' } = this.dataSource;
      const triggers = ['click', 'hover'];

      if (this.viewType === viewTypes.historySnowSeasonal) {
        return null;
      }

      if (this.viewType === viewTypes.snowDepth) {
        return {
          content: this.location?.snow_depth_seasonal?.source_attribution || `Reported by ${name}`,
          triggers,
        };
      }

      let content;
      let field;

      const getDefaultContent = () => {
        if (this.viewType === viewTypes.currentWeather || this.viewType === viewTypes.forecast) {
          return `Forecast by ${name}`;
        }

        if (this.isEstimate) {
          return `Estimated by ${name}. If locations do not report an official snow total, we use a proprietary method to provide an estimate. These estimates are only available to All-Access members.`;
        }

        return `Reported by ${name}. ${this.$config.siteName} does not verify this data. Resorts are responsible for any errors.`;
      };

      if (this.viewType === viewTypes.currentWeather) {
        field = 'forecast_current';
      }
      else if (this.viewType === viewTypes.forecast) {
        // NOTE: The forecast view is currently only used by the <Snow10DaySemiDailyForecastCard>
        // which displays the forecast_semi_daily values in a table. So for now, the
        // forecast_semi_daily field is simply used here. In the future, if other forecast views
        // are added, then a different field will be used here!
        field = 'forecast_semi_daily';
      }
      else if (this.viewType === viewTypes.newSnow || this.viewType === viewTypes.history) {
        field = 'history_snow_daily';
      }
      else if (this.viewType === viewTypes.resortReport) {
        field = 'resort_report';
      }

      if (field) {
        const attribution = this.getAttribution(this.dataSource.id, field);
        content = attribution?.detail;
      }

      if (!content) {
        content = getDefaultContent();
      }

      return { content, triggers };
    },
  },
};
</script>
